import { z } from 'zod';
import {
  CREATOR_REVENUE_SNAPSHOT_MAX_RANGE_DAYS,
  CREATOR_REVENUE_SNAPSHOT_MAX_RANGE_MS,
  PAN_DOCUMENT_STATUS,
  PAYOUT_STATUS,
  PAYOUT_METHOD,
  type PanDocumentStatus,
} from '@artha/types';

export const LEADERBOARD_PERIOD = {
  WEEK: 'week',
  MONTH: 'month',
} as const;

export const LEADERBOARD_SORT_BY = {
  EARNINGS: 'earnings',
  CLICKS: 'clicks',
} as const;

// Revenue Schemas
export const RevenueTimeSeriesQuerySchema = z.object({
  groupBy: z.enum(['week', 'month']),
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(12),
});

export const RevenueMetricsResponseSchema = z.object({
  totalRevenue: z.number(),
  totalClicks: z.number(),
  pendingPayouts: z.number(),
  successfulPayouts: z.number(),
  currentMonthRevenue: z.number(),
  lastMonthRevenue: z.number(),
  grossEarnings: z.number(),
  platformFees: z.number(),
  pendingAmount: z.number(),
  dueAmount: z.number(),
  revenueGrowth: z.number(),
  clicksGrowth: z.number().optional(),
  currency: z.string().optional(),
});

export const DashboardMetricsResponseSchema = z.object({
  totalEarnings: z.number(),
  thisWeekEarnings: z.number(),
  thisMonthEarnings: z.number(),
  pendingAmount: z.number(),
  totalClicks: z.number(),
  currency: z.string(),
  missedEarningsBanner: z.object({
    daysSinceLastShare: z.number().int().nonnegative(),
    estimatedUnclaimed: z.number().nonnegative(),
  }).optional(),
});

export const RevenueImpactParamsSchema = z.object({
  creatorId: z.string().min(1),
});

export const RevenueImpactResponseSchema = z.object({
  clicks: z.number(),
  applications: z.number(),
  isEstimated: z.boolean(),
  hasMeaningfulActivity: z.boolean(),
  monthName: z.string().optional(),
});

export const LeaderboardQuerySchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
  period: z.enum([LEADERBOARD_PERIOD.WEEK, LEADERBOARD_PERIOD.MONTH]).default(LEADERBOARD_PERIOD.WEEK),
  sortBy: z.enum([LEADERBOARD_SORT_BY.EARNINGS, LEADERBOARD_SORT_BY.CLICKS]).default(LEADERBOARD_SORT_BY.EARNINGS),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
});

export const LeaderboardEntrySchema = z.object({
  creatorId: z.string(),
  name: z.string(),
  avatarUrl: z.string().optional(),
  earnings: z.number(),
  clicks: z.number(),
  rank: z.number(),
  isAnonymous: z.boolean(),
});

export const LeaderboardResponseSchema = z.object({
  topCreators: z.array(LeaderboardEntrySchema),
  rankings: z.array(LeaderboardEntrySchema),
  currentUser: z.object({
    rank: z.number(),
    earnings: z.number(),
    clicks: z.number(),
    isAnonymous: z.boolean(),
  }).optional(),
  pagination: z.object({
    itemCount: z.number(),
    perPage: z.number(),
    currentPage: z.number(),
    pageCount: z.number(),
  }),
});

// Payout Schemas
export const PayoutDetailsSchema = z.discriminatedUnion('method', [
  z.object({
    method: z.literal(PAYOUT_METHOD.BANK),
    country: z.string().length(2), // ISO country code
    currency: z.string().length(3), // ISO currency code
    account_type: z.enum(['savings', 'current']).optional(),
    address: z.string().optional(),
    details: z.object({
      beneficiary_name: z.string().min(1),
      bank_account: z.string().min(1),
      bank_name: z.string().optional(),
      ifsc: z.string().optional(),
      routing_number: z.string().optional(),
      swift_code: z.string().optional(),
      pan: z.string().optional(),
    }).passthrough(),
  }),
  z.object({
    method: z.literal(PAYOUT_METHOD.PAYPAL),
    country: z.string().length(2), // ISO country code
    currency: z.string().length(3), // ISO currency code
    address: z.string().optional(),
    details: z.object({
      paypal_email: z.string().email(),
    }).passthrough(),
  }),
]);

export const PanDocumentMetadataSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  size: z.number(),
  uploadedAt: z.string(),
  status: z.enum(Object.values(PAN_DOCUMENT_STATUS) as [PanDocumentStatus, ...PanDocumentStatus[]])
    .default(PAN_DOCUMENT_STATUS.PENDING_REVIEW),
});

export const PanDocumentViewSchema = z.object({
  document: PanDocumentMetadataSchema,
  viewUrl: z.string().url(),
  expiresAt: z.string(),
});

export const PayoutCreateSchema = z.object({
  payout_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  amount: z.number().positive().multipleOf(0.01),
  currency: z.string().length(3).default('USD'),
  payout_method: z.nativeEnum(PAYOUT_METHOD),
  status: z.enum(Object.values(PAYOUT_STATUS) as [string, ...string[]]).default(PAYOUT_STATUS.PENDING),
  reference_id: z.string().optional(),
  note: z.string().optional(),
  creator_email: z.string().email().optional(),
});

export const PayoutUpdateSchema = z.object({
  payout_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format').optional(),
  amount: z.number().positive().multipleOf(0.01).optional(),
  currency: z.string().length(3).optional(),
  payout_method: z.nativeEnum(PAYOUT_METHOD).optional(),
  status: z.enum(Object.values(PAYOUT_STATUS) as [string, ...string[]]).optional(),
  reference_id: z.string().optional(),
  note: z.string().optional(),
}).refine(data => Object.keys(data).length > 0, {
  message: 'At least one field must be provided for update',
});

export const PayoutListQuerySchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(10),
  status: z.enum(Object.values(PAYOUT_STATUS) as [string, ...string[]]).optional(),
  payout_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  search: z.string().optional(),
});

export const MarkPayoutPaidSchema = z.object({
  reference_id: z.string().min(1, 'Reference ID is required'),
});

// Import Schemas
export const PayoutImportRowSchema = z.object({
  payout_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  amount: z.number().positive().multipleOf(0.01),
  currency: z.string().length(3).default('USD'),
  payout_method: z.nativeEnum(PAYOUT_METHOD),
  status: z.enum(Object.values(PAYOUT_STATUS) as [string, ...string[]]).default(PAYOUT_STATUS.PENDING),
  reference_id: z.string().optional(),
  note: z.string().optional(),
});

export const PayoutManualImportSchema = z.object({
  data: z.array(PayoutImportRowSchema).min(1),
});

export const SyncMilestonesSchema = z.object({
  creatorId: z.string().min(1, 'creatorId is required'),
});

// Creator revenue snapshot (CRM-key protected, consumed by the Artha support tool).
// Window is a pair of UTC calendar days, inclusive, exactly how the creator Earnings page
// defines its weeks (Sunday to Saturday in UTC).
const CALENDAR_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const calendarDay = (field: string) =>
  z.string().regex(CALENDAR_DAY_PATTERN, `${field} must be a calendar day (YYYY-MM-DD)`).refine(
    (value) => !Number.isNaN(new Date(`${value}T00:00:00Z`).getTime()),
    `${field} must be a valid calendar day`,
  );

export const CreatorRevenueSnapshotQuerySchema = z
  .object({
    weekStart: calendarDay('weekStart'),
    weekEnd: calendarDay('weekEnd'),
  })
  .superRefine((value, ctx) => {
    const rangeMs = new Date(`${value.weekEnd}T00:00:00Z`).getTime() - new Date(`${value.weekStart}T00:00:00Z`).getTime();
    if (rangeMs < 0) {
      ctx.addIssue({ code: 'custom', path: ['weekEnd'], message: 'weekEnd must be on or after weekStart' });
      return;
    }
    if (rangeMs >= CREATOR_REVENUE_SNAPSHOT_MAX_RANGE_MS) {
      ctx.addIssue({
        code: 'custom',
        path: ['weekEnd'],
        message: `Range must be ${CREATOR_REVENUE_SNAPSHOT_MAX_RANGE_DAYS} days or shorter`,
      });
    }
  });

export const CreatorRevenueSnapshotEntrySchema = z.object({
  creatorId: z.string(),
  username: z.string().nullable(),
  displayName: z.string().nullable(),
  clientName: z.string(),
  // Requested window as the creator's Earnings page shows it (gross, before platform fee):
  // the same per-creator aggregate the weekly card uses.
  earnings: z.number(),
  clicks: z.number(),
  // Same window counting every valid click, i.e. what the creator saw while the week was
  // still live, before unapproved clicks stopped counting at week close.
  liveEarnings: z.number(),
  // Current calendar month as the creator dashboard shows it (gross), and how much of
  // that figure comes from the requested window. Null when the dashboard metrics could
  // not be loaded for this creator.
  monthlyEarnings: z.number().nullable(),
  reportWeekInMonthlyEarnings: z.number().nullable(),
});

export const CreatorRevenueSnapshotResponseSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  monthStart: z.string().datetime(),
  generatedAt: z.string().datetime(),
  creators: z.array(CreatorRevenueSnapshotEntrySchema),
});

// Type exports
export type RevenueTimeSeriesQuery = z.infer<typeof RevenueTimeSeriesQuerySchema>;
export type RevenueMetricsResponse = z.infer<typeof RevenueMetricsResponseSchema>;
export type DashboardMetricsResponse = z.infer<typeof DashboardMetricsResponseSchema>;
export type PayoutDetails = z.infer<typeof PayoutDetailsSchema>;
export type PanDocumentMetadata = z.infer<typeof PanDocumentMetadataSchema>;
export type PanDocumentView = z.infer<typeof PanDocumentViewSchema>;
export type PayoutCreate = z.infer<typeof PayoutCreateSchema>;
export type PayoutUpdate = z.infer<typeof PayoutUpdateSchema>;
export type PayoutListQuery = z.infer<typeof PayoutListQuerySchema>;
export type MarkPayoutPaid = z.infer<typeof MarkPayoutPaidSchema>;
export type PayoutImportRow = z.infer<typeof PayoutImportRowSchema>;
export type PayoutManualImport = z.infer<typeof PayoutManualImportSchema>;
export type LeaderboardQuery = z.infer<typeof LeaderboardQuerySchema>;
export type LeaderboardResponse = z.infer<typeof LeaderboardResponseSchema>;
export type SyncMilestones = z.infer<typeof SyncMilestonesSchema>;
export type CreatorRevenueSnapshotQuery = z.infer<typeof CreatorRevenueSnapshotQuerySchema>;
export type CreatorRevenueSnapshotEntry = z.infer<typeof CreatorRevenueSnapshotEntrySchema>;
export type CreatorRevenueSnapshotResponse = z.infer<typeof CreatorRevenueSnapshotResponseSchema>;
